import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';
import axios from 'axios';

const Login = ({ onSuccess }) => {
    const [selfState, setSelfState] = useState({
        api_key: process.env.REACT_APP_API_KEY || '',
        user_id: process.env.REACT_APP_USER_ID || '',
        password: process.env.REACT_APP_PASSWORD || '',
        totpToken: '',
        error: '',
        isLoading: false,
    });

    const [showPassword, setShowPassword] = useState(false);

    const validate = async () => {
        const trimmedState = {
            api_key: selfState.api_key.trim(),
            user_id: selfState.user_id.trim(),
            password: selfState.password.trim(),
            totpToken: selfState.totpToken.trim(),
        };

        if (!/^\d{6}$/.test(trimmedState.totpToken)) {
            setSelfState({ ...selfState, error: 'TOTP must be a 6-digit number', isLoading: false });
            return;
        }

        setSelfState({ ...selfState, error: '', isLoading: true });

        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/auth`, {
                api_key: trimmedState.api_key,
                user_id: trimmedState.user_id,
                password: trimmedState.password,
                totpToken: trimmedState.totpToken,
            });
            const { userProfile } = response.data;
            sessionStorage.setItem('userInfo', JSON.stringify(userProfile));
            onSuccess(userProfile);
        } catch (error) {
            const errorMessage = error.response?.data ||
                (error.code === 'ERR_NETWORK' ? 'Server unreachable. Please check your connection.' : 'Authentication failed');
            setSelfState({
                ...selfState,
                error: errorMessage,
                isLoading: false,
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        validate();
    };

    const isFormValid = selfState.api_key.trim() && selfState.user_id.trim() && selfState.password.trim() && selfState.totpToken.trim();

    return (
        <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
            <Row className="w-100">
                <Col xs={12} sm={8} md={6} lg={6} className="mx-auto">
                    <Card className="shadow-lg border-0">
                        <Card.Body className="p-5">
                            <div className="text-center mb-4">
                                <h2 className="fw-bold text-primary mb-2">Welcome Back, Joe</h2>
                                <p className="text-muted">Please log in to your account</p>
                            </div>

                            {selfState.error && (
                                <Alert variant="danger" className="mb-4">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    {selfState.error}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-key-fill me-2"></i>
                                        API Key
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter your API key"
                                        value={selfState.api_key}
                                        onChange={(e) =>
                                            setSelfState({ ...selfState, api_key: e.target.value.trim() })
                                        }
                                        required
                                        disabled={selfState.isLoading}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-person-fill me-2"></i>
                                        User ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter your user ID"
                                        value={selfState.user_id}
                                        onChange={(e) =>
                                            setSelfState({ ...selfState, user_id: e.target.value.trim() })
                                        }
                                        required
                                        disabled={selfState.isLoading}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-lock-fill me-2"></i>
                                        Password
                                    </Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Enter your password"
                                            value={selfState.password}
                                            onChange={(e) =>
                                                setSelfState({ ...selfState, password: e.target.value.trim() })
                                            }
                                            required
                                            disabled={selfState.isLoading}
                                        />
                                        <Button
                                            variant="link"
                                            className="position-absolute top-50 end-0 translate-middle-y border-0 text-muted"
                                            style={{ zIndex: 5 }}
                                            onClick={() => setShowPassword(!showPassword)}
                                            disabled={selfState.isLoading}
                                        >
                                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                        </Button>
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-shield-lock-fill me-2"></i>
                                        TOTP Code
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter 6-digit code"
                                        value={selfState.totpToken}
                                        onChange={(e) =>
                                            setSelfState({ ...selfState, totpToken: e.target.value.trim() })
                                        }
                                        maxLength="6"
                                        required
                                        disabled={selfState.isLoading}
                                    />
                                    <Form.Text className="text-muted">
                                        Enter the 6-digit code from your authenticator app
                                    </Form.Text>
                                </Form.Group>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    className="w-100 fw-semibold"
                                    disabled={!isFormValid || selfState.isLoading}
                                >
                                    {selfState.isLoading ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-box-arrow-in-right me-2"></i>
                                            Log In
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Login;